import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Grid,
  Stack,
  Paper,
  styled,
  linearProgressClasses,
  Tooltip,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import SyncIcon from "@mui/icons-material/Sync";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import SummarizeIcon from "@mui/icons-material/Summarize";
import InfoIcon from "@mui/icons-material/Info";
import { palette, theme as colorTheme } from "../../../../Convoze.theme";




const primary = colorTheme('palette', 'primary').value
const primaryBg = colorTheme('palette', 'primaryBg').value
const positive = colorTheme('palette', 'positive').value
const positiveBg = colorTheme('palette', 'positiveBg').value
const negative = colorTheme('palette', 'negative').value
const negativeBg = colorTheme('palette', 'negativeBg').value
const neutral = colorTheme('palette', 'neutral').value
const neutralBg = colorTheme('palette', 'neutralBg').value
const agent = colorTheme('palette', 'agent').value
const agentBg = colorTheme('palette', 'agentBg').value





const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    ...theme.applyStyles("dark", {
      backgroundColor: primary
    }),
  },
}));
// Function to determine color based on value
const getSentimentColor = (value) => {



  if (value <= 25) return negative
  if (value <= 50) return primary
  if (value <= 75) return neutral
  return positive


};


const colors = [negativeBg, positiveBg, primaryBg, neutralBg];

const textColors = [negative, positive, primary, neutral];



const CallSummary = ({ overview, data, setValue }) => {
  return (
    <Paper elevation={1} sx={{ flex: 1, p: 2, borderRadius: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Box
          sx={{
            width: 33,
            height: 33,
            borderRadius: "50%",
            backgroundColor: primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 900,
          }}
        >
          <SummarizeIcon
            sx={{ color: "white", fontSize: 20, fontWeight: 900 }}
          />
        </Box>
        <Typography
          variant="h6"
          fontWeight={500}
          gutterBottom
          display={"flex"}
          gap={1}
        >
          Call Summary
        </Typography>
        <Tooltip
          title="Gives a breif summary of the conversation mentioning the keywords used in the conversation and the intent of the call and the outcome of the call."
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InfoIcon
            sx={{
              fontSize: 18,
              color: "grey",
            }}
          />
        </Tooltip>
      </Stack>
      {/* Summary Card */}
      <Box
        sx={{
          mb: 3,
          mt: 2,
          borderRadius: 3,
          bgcolor: "#f9fafb",
          border: "1px solid #e5e7eb",
        }}
      >
        <CardContent>
          {overview?.AutoGeneratedCallSummary ? (
            <Typography variant="body2" color="#526376">
              {overview?.AutoGeneratedCallSummary}
            </Typography>
          ) : (
            <Chip
              label="No data available."
              size="small"
              sx={{
                mt: 2,
                backgroundColor: "#E4E5E7",
                color: "#5F6062",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: "4px",
              }}
            />
          )}
        </CardContent>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {/* Detected Issues */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: "100%",
              // mb: 3,
              borderRadius: 3,
              bgcolor: "#f9fafb",
              border: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography variant="body1" gutterBottom>
                  Detected Issue
                </Typography>
                <Tooltip
                  title="These are the issues detected by Convoze after analyzing the conversation."
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoIcon
                    sx={{
                      fontSize: 18,
                      color: "grey",
                    }}
                  />
                </Tooltip>
              </Stack>
              <Box
                gap={1}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  whiteSpace: "nowrap",
                  gap: 3,
                  // mt: 2
                }}
              >
                {overview?.DetectedIssues?.length > 0 ? (
                  overview?.DetectedIssues?.map((e, index) => (
                    <Chip
                      key={index}
                      variant="contained"
                      label={e}
                      sx={{
                        backgroundColor: colors[index % colors.length],
                        color: "black",
                        border: `1px solid ${colors[index % colors.length]}`,
                        height: "auto",
                        minHeight: "unset",
                        minWidth: "unset",
                        padding: "2px 6px",
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        fontWeight: 550,
                      }}
                    />
                  ))
                ) : (
                  <Chip
                    label="No detected issues were generated for this call"
                    size="small"
                    sx={{
                      mt: 2,
                      backgroundColor: "#E4E5E7",
                      color: "#5F6062",
                      fontWeight: 500,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Box>
        </Grid>

        {/* Sentiment Scores */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: "100%",
              // mb: 3,
              borderRadius: 3,
              bgcolor: "#f9fafb",
              border: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
              // backgroundColor: "yellow",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3.5,
                // justifyContent: "space-between",
              }}
            >
              <Stack
                direction={{ md: "row", xl: "row" }}
                spacing={1}
                justifyContent="space-between"
              >
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  Sentiment Scores
                  <Tooltip
                    title="Gives the scores on the basis of sentimental analysis."
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InfoIcon
                      sx={{
                        fontSize: 18,
                        color: "grey",
                      }}
                    />
                  </Tooltip>
                </Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {[
                    { label: "Poor", color: negative },
                    { label: "Average", color: primary },
                    { label: "Good", color: neutral },
                    { label: "Excellent", color: positive },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                        }}
                      />
                      <Typography sx={{ fontSize: 10 }} variant="caption">
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography onClick={() => setValue(1)} variant="body2" color="grey" sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}>
                    Customer - {overview?.SentimentScores?.Customer?.score}%
                  </Typography>
                  <Stack spacing={2} sx={{ flexGrow: 1, mt: 2, mb: 1 }}>
                    <BorderLinearProgress
                      variant="determinate"
                      value={overview?.SentimentScores?.Customer?.score}
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getSentimentColor(
                            overview?.SentimentScores?.Agent?.score
                          ),
                        },
                      }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography onClick={() => setValue(2)} variant="body2" color="grey" sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}>
                    Agent - {overview?.SentimentScores?.Agent?.score}%
                  </Typography>
                  <Stack spacing={2} sx={{ flexGrow: 1, mt: 2 }}>
                    <BorderLinearProgress
                      variant="determinate"
                      value={overview?.SentimentScores?.Agent?.score}
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: getSentimentColor(
                            overview?.SentimentScores?.Agent?.score
                          ),
                        },
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </Grid>
      </Grid>

      {/* Highlighted Keywords */}
      {/* <Box
        sx={{
          mt: 3,
          mb: 1,
          borderRadius: 3,
          bgcolor: "#f9fafb",
          border: "1px solid #e5e7eb",
        }}
      >
        <CardContent>
          <Typography
            variant="body1"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            Highlighted Keywords &amp; Phrases
            <Tooltip
              title="Words most frequently used in the conversation."
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoIcon
                sx={{
                  fontSize: 18,
                  color: "grey",
                }}
              />
            </Tooltip>
          </Typography>
          <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
            {overview?.HighlightedKeywordsAndPhrases?.keywords?.length > 0 ? (
              overview?.HighlightedKeywordsAndPhrases?.keywords?.map(
                (e, index) => (
                  <Chip
                    size="small"
                    label={`${e}`}
                    sx={{
                      backgroundColor: colors[index % colors.length],
                      color: textColors[index % textColors.length],
                    }}
                  />
                )
              )
            ) : (
              <Chip
                label="No data available."
                size="small"
                sx={{
                  mt: 2,
                  backgroundColor: "#E4E5E7",
                  color: "#5F6062",
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "4px",
                }}
              />
            )}
          </Stack>
        </CardContent>
      </Box> */}
      {/* Flag & Alert Indicators */}
      {/* <Grid item xs={12} md={12}>
        <Paper elevation={1} sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body1" gutterBottom>
              🚨 &nbsp;Flag & Alert Indicators
            </Typography>

            <Box
              spacing={2}
              mt={2}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#FFF7D6",
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  alignContent: "flex-start",
                }}
              >
                <FlagIcon
                  sx={{
                    color: "#ffa061",
                    fontSize: 16,
                  }}
                  size={12}
                />
                <Box>
                  <Typography variant="subtitle2" color="warning.dark">
                    Escalation Flag
                  </Typography>
                  <Typography variant="body2">
                    {overview?.FlagsAndAlerts?.EscalationFlag}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: "#FFE4E4",
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  alignContent: "flex-start",
                }}
              >
                <WarningIcon
                  sx={{
                    color: "red",
                    fontSize: 16,
                  }}
                  size={12}
                />
                <Box>
                  <Typography variant="subtitle2" color="warning.dark">
                    Repeat Complaint
                  </Typography>
                  <Typography variant="body2">
                    {overview?.FlagsAndAlerts?.RepeatComplaint}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  alignContent: "flex-start",
                }}
              >
                <SyncIcon
                  sx={{
                    color: "#b3b3b3",
                    fontSize: 16,
                  }}
                  size={12}
                />
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Reopened Call
                  </Typography>
                  <Typography variant="body2">
                    {overview?.FlagsAndAlerts?.ReopenedCall}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  alignContent: "flex-start",
                }}
              >
                <ErrorIcon
                  sx={{
                    color: "#b3b3b3",
                    fontSize: 16,
                  }}
                  size={12}
                />
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Compliance Violation
                  </Typography>
                  <Typography variant="body2">
                    {overview?.FlagsAndAlerts?.ComplianceViolation}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Paper>
      </Grid> */}
    </Paper>
  );
};

export default CallSummary;
